import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getModalScale, getViewportHeight } from './utils/modalPositioning';
import { useDeviceDetection } from '../DynamicWrapper/hooks/useDeviceDetection';

/**
 * Compute the transform string based on animation state.
 */
const _computeTransform = ({ animationState, isDesktop, modalScale, viewportH, translateFn, index }) => {
  const startTransform = isDesktop
    ? `translateY(-80px) scale(${modalScale})`
    : `translateY(${viewportH}px) scale(${modalScale})`;

  if (animationState === 'exiting') {
    return isDesktop
      ? `translateY(-100%) scale(${modalScale})`
      : `translateY(${viewportH}px) scale(${modalScale})`;
  }

  if (animationState === 'mounting') {
    return startTransform;
  }

  if (animationState === 'entering' || animationState === 'open') {
    const defaultY = isDesktop ? 60 : viewportH - 300;
    const y = translateFn ? translateFn(index) : defaultY;
    return `translateY(${y}px) scale(${modalScale})`;
  }

  return undefined;
};

/**
 * Build space-separated CSS class string, filtering falsy values.
 */
const _buildClassNames = (...parts) => parts.filter(Boolean).join(' ');

/**
 * ModalLayer renders a single modal with its overlay (when applicable).
 */
const ModalLayer = ({ modal, index, stackSize, isDesktop, wrapperHeight, getModalRef, onClose, onUpdate, translateFn, closeIconSrc, usePortal, showOverlay }) => {
  const modalRef = useRef(null);
  const isEntering = modal.animationState === 'entering';
  const isOpen = modal.animationState === 'open';
  const isExiting = modal.animationState === 'exiting';
  const isVisible = isOpen || isEntering;
  const isTopModal = index === stackSize - 1;
  const isDesktopDevice = useDeviceDetection();
  const modalScale = getModalScale(index, stackSize, usePortal);

  useEffect(() => {
    if (getModalRef) getModalRef(index, modalRef.current);
  }, [modalRef.current]);

  // Stable references for the modal component's own closeModal/updateModal
  // props. onClose/onUpdate (useModalManager's closeModal/updateModal) are
  // already memoized with an empty dep array, so as long as `index` doesn't
  // change these stay referentially stable across re-renders. Without this,
  // recreating them inline on every render (a new arrow function each time)
  // breaks any consumer that lists updateModal/closeModal in a useEffect's
  // dependency array (e.g. NewDocument's `useEffect(() => updateModal({...}),
  // [isSending, updateModal])`): the new reference re-triggers the effect,
  // which calls updateModal → setModalStack → re-renders this layer → a new
  // reference again, forever. That runaway loop starves the animation state
  // machine's setTimeouts, leaving modals stuck mid-transition (wrong scale,
  // hidden close button) instead of ever reaching a stable "open" state.
  const closeModalForComponent = useCallback(
    (callbackData) => onClose(index, callbackData),
    [onClose, index],
  );
  const updateModalForComponent = useCallback(
    (patch) => onUpdate?.(index, patch),
    [onUpdate, index],
  );

  const viewportH = getViewportHeight();
  const transform = _computeTransform({
    animationState: modal.animationState,
    isDesktop,
    modalScale,
    viewportH,
    translateFn,
    index,
  });

  const Component = modal.component;

  const contentClasses = _buildClassNames(
    'dw-modal-content',
    usePortal && 'dw-modal-content--portal',
    isVisible && 'dw-modal-content--visible',
    isDesktopDevice && 'wrapper--desktop',
    isExiting && (isDesktopDevice ? 'dw-modal-content--exiting-desktop' : 'dw-modal-content--exiting-mobile'),
  );

  const overlayClasses = _buildClassNames(
    'dw-modal-overlay',
    usePortal && 'dw-modal-overlay--portal',
    isVisible && 'dw-modal-overlay--visible',
  );

  return (
    <>
      {showOverlay && (
        <div className={overlayClasses} id={`dynamic-modal-overlay-${index}`} />
      )}
      <div
        ref={modalRef}
        className={contentClasses}
        id={`dynamic-modal-${index}`}
        style={{ transform }}
      >
      <div className="dw-modal-header">
        <h1 className="dw-modal-title">
          {modal.title || ''}
        </h1>
        {!modal.hideCloseButton && isTopModal && !isExiting && (
          <button
            className="dw-modal-close-btn"
            onClick={() => onClose(index, modal.closeData)}
          >
            {closeIconSrc
              ? <img src={closeIconSrc} alt="close" />
              : <span>✕</span>
            }
          </button>
        )}
      </div>
      <div className="dw-modal-body">
        {Component && (
          <Component
            data={modal.data}
            closeModal={closeModalForComponent}
            updateModal={updateModalForComponent}
          />
        )}
      </div>
    </div>
    </>
  );
};

ModalLayer.propTypes = {
  modal: PropTypes.shape({
    _id: PropTypes.number,
    animationState: PropTypes.string,
    component: PropTypes.elementType,
    title: PropTypes.string,
    hideCloseButton: PropTypes.bool,
    data: PropTypes.object,
    closeData: PropTypes.any,
  }).isRequired,
  index: PropTypes.number.isRequired,
  stackSize: PropTypes.number.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  wrapperHeight: PropTypes.number,
  getModalRef: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
  translateFn: PropTypes.func,
  closeIconSrc: PropTypes.string,
  usePortal: PropTypes.bool,
  showOverlay: PropTypes.bool,
};

export default ModalLayer;
